import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { UsersRound, Plus, Save, Archive, Search } from "lucide-react";

export default function DoctorGroups() {
  const [groups, setGroups] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", doctor_ids: [], active: true });
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [relDoctor, setRelDoctor] = useState("");
  const [relIds, setRelIds] = useState([]);
  const [relBusy, setRelBusy] = useState(false);

  const load = async () => {
    try {
      const [g, d] = await Promise.all([
        api.get("/doctor-groups"),
        api.get("/doctors").catch(() => ({ data: [] })),
      ]);
      setGroups(Array.isArray(g.data) ? g.data : []);
      setDoctors(Array.isArray(d.data) ? d.data : []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredDoctors = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return doctors;
    return doctors.filter((d) =>
      [d.name, d.full_name, d.email, d.specialization]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [doctors, q]);

  const selectGroup = (g) => {
    setSelected(g);
    setForm({
      name: g.name || "",
      description: g.description || "",
      doctor_ids: g.doctor_ids || [],
      active: g.active !== false,
    });
  };

  const newGroup = () => {
    setSelected(null);
    setForm({ name: "", description: "", doctor_ids: [], active: true });
  };

  const toggleDoctor = (id) => {
    const existing = form.doctor_ids || [];
    setForm({
      ...form,
      doctor_ids: existing.includes(id)
        ? existing.filter((x) => x !== id)
        : [...existing, id],
    });
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Group name is required");
      return;
    }

    setBusy(true);
    try {
      if (selected?.id) {
        await api.put(`/doctor-groups/${selected.id}`, form);
        toast.success("Doctor group updated");
      } else {
        await api.post("/doctor-groups", form);
        toast.success("Doctor group created");
      }
      await load();
      newGroup();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  const archiveGroup = async () => {
    if (!selected?.id) return;
    if (!window.confirm(`Archive ${selected.name}?`)) return;

    setBusy(true);
    try {
      await api.delete(`/doctor-groups/${selected.id}`);
      toast.success("Doctor group archived");
      await load();
      newGroup();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  const doctorName = (id) => {
    const d = doctors.find((x) => x.id === id || x.email === id);
    return d?.name || d?.full_name || d?.email || id;
  };

  const selectRelDoctor = (id) => {
    setRelDoctor(id);
    const d = doctors.find((x) => x.id === id);
    setRelIds(d?.reliever_ids || []);
  };
  const toggleRel = (id) => {
    setRelIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const saveRelievers = async () => {
    if (!relDoctor) return;
    setRelBusy(true);
    try {
      await api.put(`/doctors/${relDoctor}/relievers`, { reliever_ids: relIds });
      toast.success("Relievers updated");
      await load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setRelBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="doctor-groups-page">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>
            Doctor Groups
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage shared patient pools, relievers, and department-level patient access.
          </p>
        </div>

        <Button onClick={newGroup} className="bg-blue-700 hover:bg-blue-800">
          <Plus size={16} className="mr-2" />
          New Group
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-5">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <UsersRound size={17} />
              Groups
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[72vh] overflow-auto pr-1">
              {groups.map((g) => (
                <button
                  key={g.id}
                  onClick={() => selectGroup(g)}
                  className={`w-full text-left rounded-xl border p-4 transition ${
                    selected?.id === g.id ? "border-blue-300 bg-blue-50" : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-bold text-slate-900">{g.name}</div>
                      <div className="text-xs text-slate-500 mt-1">{g.description || "No description"}</div>
                      <div className="text-xs text-slate-400 mt-2">{(g.doctor_ids || []).length} member(s)</div>
                    </div>
                    {g.active === false ? <Badge variant="outline">Archived</Badge> : <Badge className="bg-emerald-600">Active</Badge>}
                  </div>
                </button>
              ))}

              {groups.length === 0 ? (
                <div className="text-center text-sm text-slate-400 py-8">No doctor groups yet.</div>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {selected ? `Manage ${selected.name}` : "Create Doctor Group"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Group Name</div>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Legacy Shared Doctors"
                />
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Status</div>
                <select
                  value={form.active ? "active" : "archived"}
                  onChange={(e) => setForm({ ...form, active: e.target.value === "active" })}
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
                >
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Description</div>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full min-h-[80px] rounded-lg border border-slate-200 p-3 text-sm"
                placeholder="Example: Current doctors who already shared patients before access enforcement."
              />
            </div>

            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500">Members</div>
                <Badge variant="outline">{(form.doctor_ids || []).length} selected</Badge>
              </div>

              <div className="relative mb-3">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search doctors..."
                  className="pl-9"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[42vh] overflow-auto pr-1">
                {filteredDoctors.map((d) => {
                  const id = d.id || d.email;
                  const checked = (form.doctor_ids || []).includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggleDoctor(id)}
                      className={`rounded-xl border p-3 text-left ${
                        checked ? "border-blue-300 bg-blue-50" : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 text-sm truncate">
                            {d.name || d.full_name || d.email}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {d.specialization || d.email || "Doctor"}
                          </div>
                        </div>
                        {checked ? <Badge className="bg-blue-700">Member</Badge> : <Badge variant="outline">Add</Badge>}
                      </div>
                    </button>
                  );
                })}

                {filteredDoctors.length === 0 ? (
                  <div className="text-sm text-slate-400 py-6">No doctors found.</div>
                ) : null}
              </div>
            </div>

            {(form.doctor_ids || []).length ? (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Selected Members</div>
                <div className="flex flex-wrap gap-2">
                  {(form.doctor_ids || []).map((id) => (
                    <Badge key={id} variant="outline">{doctorName(id)}</Badge>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="flex flex-col md:flex-row gap-2">
              <Button onClick={save} disabled={busy} className="flex-1 h-11 bg-blue-700 hover:bg-blue-800">
                <Save size={16} className="mr-2" />
                Save Group
              </Button>

              {selected?.id ? (
                <Button onClick={archiveGroup} disabled={busy} variant="outline" className="h-11">
                  <Archive size={16} className="mr-2" />
                  Archive
                </Button>
              ) : null}
            </div>

            {selected?.id?.startsWith("legacy_shared_") ? (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
                This is a legacy shared group. Existing doctors keep current shared patient access.
                New doctors are not automatically added unless an admin adds them here.
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6" data-testid="relievers-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <UsersRound size={17} /> Relievers
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Assign a doctor's relievers. Relievers cover each other and can see each other's patients (mutual).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-md">
            <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">Doctor</div>
            <select
              value={relDoctor}
              onChange={(e) => selectRelDoctor(e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
              data-testid="reliever-doctor-select"
            >
              <option value="">Select a doctor…</option>
              {doctors.map((d) => (
                <option key={d.id} value={d.id}>{d.name || d.full_name || d.email}</option>
              ))}
            </select>
          </div>

          {relDoctor ? (
            <>
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">
                  Relievers for {doctorName(relDoctor)}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[40vh] overflow-auto pr-1">
                  {doctors.filter((d) => d.id !== relDoctor).map((d) => {
                    const checked = relIds.includes(d.id);
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => toggleRel(d.id)}
                        className={`rounded-xl border p-3 text-left ${checked ? "border-blue-300 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900 text-sm truncate">{d.name || d.full_name || d.email}</div>
                            <div className="text-xs text-slate-500 truncate">{d.specialization || d.email || "Doctor"}</div>
                          </div>
                          {checked ? <Badge className="bg-blue-700">Reliever</Badge> : <Badge variant="outline">Add</Badge>}
                        </div>
                      </button>
                    );
                  })}
                  {doctors.filter((d) => d.id !== relDoctor).length === 0 ? (
                    <div className="text-sm text-slate-400 py-6">No other doctors.</div>
                  ) : null}
                </div>
              </div>
              <Button onClick={saveRelievers} disabled={relBusy} className="h-11 bg-blue-700 hover:bg-blue-800" data-testid="btn-save-relievers">
                <Save size={16} className="mr-2" /> Save Relievers
              </Button>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
